import React, { useState, useEffect } from "react";
import "../assets/css/sidebar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSidebar } from "../contexts/SideBarContext";

const Sidebar = () => {
    const { currentUser } = useAuth();
    const { isSidebarOpen, toggleSidebar } = useSidebar();
    const location = useLocation();

    // Determinar qué secciones y subsecciones deben estar abiertas según la ruta actual
    const [collapsedSections, setCollapsedSections] = useState(() => {
        const path = window.location.pathname;
        return {
            CST: path.includes('/reportes') || path.includes('/listas') || path.includes('/parametros') || path.includes('/usuarios') || path.includes('/negativizar'),
            Reports: path.startsWith('/tracking') || path.startsWith('/analysis') || path.includes('reportesCSV'),
            ADS: path.startsWith('/ads/'),
            Components: false,
            Forms: false,
            Tables: false,
        };
    });

    // Estado para subsecciones de ADS y Reports
    const [collapsedSubsections, setCollapsedSubsections] = useState(() => {
        const path = window.location.pathname;
        return {
            "chat-ai": path.includes('/ads/chat'),
            "sponsored-products": path.includes('/ads/sp-'),
            "sponsored-brands": path.includes('/ads/sb-'),
            "sponsored-display": path.includes('/ads/sd-'),
            "competitor-tracking": path.startsWith('/tracking/') || path.includes('/analysis/sales-estimate'),
            "file-analysis": path.startsWith('/analysis/') && !path.includes('/analysis/sales-estimate'),
        };
    });

    // Helpers para determinar el elemento activo
    const isItemActive = (href) => {
        if (!href) return false;
        if (location.pathname === href) return true;
        if (href === '/tracking/media' && location.pathname.startsWith('/tracking/media')) return true;
        if (href !== '/' && href !== '/dashboard' && location.pathname.startsWith(href + '/')) return true;
        return false;
    };

    const isSubsectionActive = (subsection) => {
        return Boolean(subsection.items && subsection.items.some(item => isItemActive(item.href)));
    };

    const isSectionActive = (section) => {
        if (section.items && section.items.some(item => isItemActive(item.href))) return true;
        if (section.subsections && section.subsections.some(sub => isSubsectionActive(sub))) return true;
        return false;
    };

    // Estado para la cuenta activa
    const [selectedAccountName, setSelectedAccountName] = useState('Not selected');
    const [selectedAccountId, setSelectedAccountId] = useState('N/A');

    // Escuchar cambios en localStorage
    useEffect(() => {
        const updateAccountInfo = () => {
            const accountName = localStorage.getItem('selectedAccountName') || 'Not selected';
            const accountId = localStorage.getItem('selectedAccountValue') || 'N/A';
            setSelectedAccountName(accountName);
            setSelectedAccountId(accountId);
        };

        // Actualizar al montar
        updateAccountInfo();

        // Escuchar cambios en localStorage
        window.addEventListener('storage', updateAccountInfo);

        // Escuchar evento personalizado para cambios en la misma pestana
        window.addEventListener('accountChanged', updateAccountInfo);

        return () => {
            window.removeEventListener('storage', updateAccountInfo);
            window.removeEventListener('accountChanged', updateAccountInfo);
        };
    }, []);

    const navigate = useNavigate();

    const handleLogout = () => {
        navigate("/login");
    };

    const toggleCollapse = (section) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const toggleSubsection = (subsection) => {
        setCollapsedSubsections((prev) => ({
            ...prev,
            [subsection]: !prev[subsection],
        }));
    };

    // Estructura de subsecciones para ADS
    const adsSubsections = [
        /*
        {
            id: "chat-ai",
            icon: "bi bi-chat-dots",
            label: "CHAT AI ASSISTANT",
            items: [
                { label: "Sponsored Products Chat", href: "/ads/sponsored-products", icon: "bi-chat-left-text", description: "AI assistant specialized in Sponsored Products strategies and campaign creation." },
                { label: "Sponsored Brands Chat", href: "/ads/sponsored-brands", icon: "bi-chat-left-text", description: "AI assistant specialized in Sponsored Brands strategies and campaign creation." },
                { label: "Sponsored Display Chat", href: "/ads/sponsored-display", icon: "bi-chat-left-text", description: "AI assistant specialized in Sponsored Display strategies and campaign creation." },
            ]
        },
        */
        {
            id: "sponsored-products",
            icon: "bi bi-megaphone",
            label: "SPONSORED PRODUCTS",
            items: [
                { label: "Single keyword campaigns", href: "/ads/sp-single-keyword", icon: "bi-search", description: "Creates campaigns targeting individual, high-performing keywords." },
                { label: "Keyword group campaigns", href: "/ads/sp-keyword-group", icon: "bi-collection", description: "Creates campaigns targeting specific groups of related keywords." },
                { label: "Product campaigns", href: "/ads/sp-product", icon: "bi-box", description: "Creates campaigns targeting specific competitor ASINs." },
                { label: "SEO Campaigns", href: "/ads/sp-product-seo", icon: "bi-graph-up", description: "Creates campaigns optimized for search engine ranking improvement." },
                { label: "Category campaigns", href: "/ads/sp-category", icon: "bi-grid", description: "Creates campaigns targeting broad Amazon product categories." },
                { label: "Brand protection keywords", href: "/ads/sp-brand-keywords", icon: "bi-shield-check", description: "Creates campaigns targeting your own brand keywords to defend against competitors." },
                { label: "Brand protection products", href: "/ads/sp-brand-products", icon: "bi-shield-plus", description: "Creates campaigns targeting your own products to defend your product pages." },
                { label: "Auto single target", href: "/ads/sp-auto-single", icon: "bi-bullseye", description: "Creates automatic campaigns with a single targeting type (e.g., Close match)." },
                { label: "Auto multi target", href: "/ads/sp-auto-multi", icon: "bi-bullseye", description: "Creates automatic campaigns utilizing multiple targeting types simultaneously." },
            ]
        },
        {
            id: "sponsored-brands",
            icon: "bi bi-badge-ad",
            label: "SPONSORED BRANDS",
            items: [
                { label: "Group keyword campaigns", href: "/ads/sb-keyword-group", icon: "bi-collection", description: "Creates Sponsored Brands campaigns targeting groups of keywords with custom creatives." },
                { label: "Single keyword campaigns", href: "/ads/sb-single-keyword", icon: "bi-search", description: "Creates Sponsored Brands campaigns targeting single keywords." },
                { label: "Vertical video campaigns", href: "/ads/sb-vertical-video", icon: "bi-camera-video", description: "Creates Sponsored Brands video campaigns optimized for vertical viewing (mobile)." },
                { label: "Horizontal video campaigns", href: "/ads/sb-horizontal-video", icon: "bi-camera-video-fill", description: "Creates Sponsored Brands video campaigns with standard horizontal formatting." },
                { label: "Product collection campaigns", href: "/ads/sb-product-collection", icon: "bi-images", description: "Creates Sponsored Brands campaigns featuring a collection of up to 3 products." },
            ]
        },
        {
            id: "sponsored-display",
            icon: "bi bi-display",
            label: "SPONSORED DISPLAY",
            items: [
                { label: "Product campaigns", href: "/ads/sd-product-campaigns", icon: "bi-box", description: "Creates Sponsored Display campaigns targeting specific competitor products or categories." },
                { label: "Audience campaigns", href: "/ads/sd-audience", icon: "bi-people", description: "Creates Sponsored Display campaigns targeting specific shopper audiences and behaviors." },
                { label: "Retargeting campaigns", href: "/ads/sd-retargeting", icon: "bi-arrow-repeat", description: "Creates Sponsored Display campaigns to re-engage shoppers who viewed your products." },
            ]
        },
    ];

    const sections = currentUser.userRolesId === 1 || currentUser.userRolesId === 2 || currentUser.userRolesId === 3 ? [
        {
            id: "CST",
            icon: "bi bi-grid",
            label: "CST Segmentation",
            items: [
                { label: "View Reports", href: "/reportes-general", icon: "bi-graph-up" },
                { label: "BlackList", href: "/listas", icon: "bi-slash-circle" },
                { label: "Express Negativizer", href: "/negativizarExpress", icon: "bi-lightning-fill" },
                { label: "Parameters", href: "/parametros", icon: "bi-sliders" },
                { label: "Users", href: "/usuarios", icon: "bi-people" }
            ]
        }
    ] : [
        {
            id: "CST",
            icon: "bi bi-grid",
            label: "CST Segmentation",
            items: [
                { label: "View Reports", href: "/reportes-general", icon: "bi-graph-up" },
                { label: "Express and CST List", href: "/listas", icon: "bi-slash-circle" },
                { label: "Parameters", href: "/parametros", icon: "bi-sliders" }
            ]
        }
    ];

    // Reports & Tracking subsections
    const reportsSubsections = [
        {
            id: "competitor-tracking",
            icon: "bi bi-binoculars",
            label: "COMPETITOR TRACKING",
            items: [
                { label: "Overview", href: "/tracking/overview", icon: "bi-speedometer2" },
                { label: "Pricing Breakdown", href: "/tracking/pricing", icon: "bi-currency-dollar" },
                { label: "Rating Evolution", href: "/tracking/rating", icon: "bi-star" },
                { label: "Ranking Evolution", href: "/tracking/ranking", icon: "bi-bar-chart-line" },
                { label: "Media Tracking", href: "/tracking/media", icon: "bi-image" },
                { label: "Best Sellers Rank", href: "/tracking/best-sellers", icon: "bi-trophy" },
                { label: "Detailed Table", href: "/tracking/detailed", icon: "bi-table" },
                { label: "Web Traffic", href: "/tracking/web-traffic", icon: "bi-globe2" },
                { label: "Sales Estimate", href: "/analysis/sales-estimate", icon: "bi-cash-coin" },
                ...(currentUser.userRolesId === 1 || currentUser.userRolesId === 2
                    ? [{ label: "Scheduler Config", href: "/tracking/scheduler-config", icon: "bi-calendar-check" }]
                    : []),
            ]
        },
        {
            id: "file-analysis",
            icon: "bi bi-file-earmark-bar-graph",
            label: "FILE ANALYSIS",
            items: [
                { label: "N-gram Analysis", href: "/analysis/ngram", icon: "bi-search" },
                { label: "Campaigns Evolution", href: "/analysis/campaigns", icon: "bi-graph-up" },
                { label: "Placements SP", href: "/analysis/placements-sp", icon: "bi-geo-alt" },
                { label: "Placements SB", href: "/analysis/placements-sb", icon: "bi-geo" },
                { label: "Parameters Analysis", href: "/analysis/parameters", icon: "bi-sliders" }
            ]
        },
    ];

    sections.push({
        id: "Reports",
        icon: "bi bi-graph-up-arrow",
        label: "Measurement & Reporting",
        items: [{
            label: "Advanced reports", href: "/reportesCSV"
        }],
        hasSubsections: true,
        subsections: reportsSubsections
    });

    // ADS ahora tiene estructura de subsecciones
    sections.push({
        id: "ADS",
        icon: "bi bi-lightning-charge",
        label: "Ads Assistant",
        showAccountInfo: true,
        accountName: selectedAccountName,
        accountId: selectedAccountId,
        hasSubsections: true,
        subsections: adsSubsections
    });

    // Auto-abrir la sección y subsección activa al cambiar de ruta
    useEffect(() => {
        sections.forEach(section => {
            if (isSectionActive(section)) {
                setCollapsedSections(prev => ({
                    ...prev,
                    [section.id]: true
                }));
            }
            if (section.subsections) {
                section.subsections.forEach(sub => {
                    if (isSubsectionActive(sub)) {
                        setCollapsedSubsections(prev => ({
                            ...prev,
                            [sub.id]: true
                        }));
                    }
                });
            }
        });
    }, [location.pathname]);

    return (
        <div>
            <i
                onClick={toggleSidebar}
                aria-label={isSidebarOpen ? "Close side menu" : "Open side menu"}
                className={`icon-sidebar bi ${isSidebarOpen ? "bi-x" : "bi-list"}`}
            ></i>

            <aside
                id="sidebar" className={`sidebar ${isSidebarOpen ? "open" : "closed"}`}
            >
                <ul className="sidebar-nav" id="sidebar-nav">
                    {sections.map((section) => {
                        const sectionActive = isSectionActive(section);
                        return (
                            <li className="nav-itemsidebar" key={section.id}>
                                <button className={`nav-link ${collapsedSections[section.id] ? "" : "collapsed"} ${sectionActive ? "has-active" : ""}`}
                                    onClick={() => toggleCollapse(section.id)}
                                    aria-expanded={collapsedSections[section.id]}
                                >
                                    <i className={section.icon}></i>
                                    <span>{section.label}</span>
                                    <i className={`bi bi-chevron-down fs-5 ms-auto ${collapsedSections[section.id] ? "rotate" : ""
                                            }`}
                                    ></i>
                                </button>
                                <div className={`nav-wrapper ${collapsedSections[section.id] ? "" : "collapsed"}`}>
                                    <div className="nav-wrapper-inner">
                                        {/* Mostrar info de cuenta solo en la seccion ADS */}
                                        {section.showAccountInfo && (
                                            <div className="ads-account-info">
                                                <div className="account-label">ACTIVE ACCOUNT</div>
                                                <div className="account-name">{section.accountName}</div>
                                                <div className="account-id">ID: {section.accountId}</div>
                                            </div>
                                        )}

                                        {/* Si tiene subsecciones (como ADS), renderizarlas */}
                                        {section.hasSubsections && section.subsections ? (
                                            <div className="ads-subsections">
                                                {section.subsections.map((subsection) => {
                                                    const subActive = isSubsectionActive(subsection);
                                                    return (
                                                        <div className="sidebar-subsection" key={subsection.id}>
                                                            <div className={`sidebar-subsection-header ${collapsedSubsections[subsection.id] ? '' : 'collapsed'} ${subActive ? 'has-active' : ''}`}
                                                                onClick={() => toggleSubsection(subsection.id)}
                                                            >
                                                                <div className="subsection-title">
                                                                    <i className={subsection.icon}></i>
                                                                    <span>{subsection.label}</span>
                                                                </div>
                                                                <i className={`bi bi-chevron-down toggle-icon ${collapsedSubsections[subsection.id] ? 'rotate' : ''}`}></i>
                                                            </div>
                                                            <div className={`sidebar-submenu-list ${collapsedSubsections[subsection.id] ? '' : 'collapsed'}`}>
                                                                <div className="sidebar-submenu-inner">
                                                                    {subsection.items.map((item, index) => {
                                                                        const active = isItemActive(item.href);
                                                                        return (
                                                                            <a 
                                                                                href={item.href} 
                                                                                className={`sidebar-menu-link ${active ? 'active' : ''}`} 
                                                                                key={index} 
                                                                                style={{ display: 'flex', alignItems: 'center' }}
                                                                            >
                                                                                <i className={`bi ${item.icon}`}></i>
                                                                                <span style={{ flex: 1 }}>{item.label}</span>
                                                                                {active && <span className="active-indicator-dot" title="Active module"></span>}
                                                                                {item.description && (
                                                                                    <i 
                                                                                        className="bi bi-info-circle ms-2" 
                                                                                        title={item.description}
                                                                                        style={{ fontSize: '0.9em', opacity: 0.6, cursor: 'help' }}
                                                                                    ></i>
                                                                                )}
                                                                            </a>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            /* Renderizado normal para secciones sin subsecciones */
                                            <ul
                                                id={`${section.id.toLowerCase()}-nav`}
                                                className="nav-content"
                                            >
                                                {section.items && section.items.map((item, index) => {
                                                    const active = isItemActive(item.href);
                                                    return (
                                                        <li key={index}>
                                                            <a href={item.href} className={active ? 'active' : ''}>
                                                                <i className={item.icon || ""}></i>
                                                                <span>{item.label}</span>
                                                                {active && <span className="active-indicator-dot" title="Active module"></span>}
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {/* Botones inferiores */}
                <div className="sidebar-footer">
                    <button className="sidebar-btn" onClick={handleLogout}>
                        <i className="bi bi-box-arrow-right"></i>
                        <span>Sign Out</span>
                    </button>
                </div>
            </aside>
        </div>
    );
};

export default Sidebar;